import captureException from 'services/captureException';
import { removePartialIntervalsDates } from 'removePartialIntervals';
import toAutoInterval from 'components/V5Gadget/toAutoInterval';

/**
 * Filters responses[0] (current) and responses[2] (previous) to remove
 * rows whose date falls in a partial interval at the edge of the date scope.
 * Mutates the responses array in place (matching original behaviour) and
 * returns it.
 */
const removePartialIntervalResponses = ({
  chartDef,
  responses,
}: {
  chartDef: V5ChartDefinition;
  responses: MetricsResponse[];
}): MetricsResponse[] => {
  if (
    !chartDef.excludePartialIntervals ||
    !chartDef.dimensionA ||
    chartDef.dimensionA.fieldType !== 'date'
  ) {
    return responses;
  }

  const dates = responses[0].map((row) => row.date) as string[];
  if (responses[0][0] === undefined) {
    return responses;
  }

  const interval = (() => {
    if (
      chartDef.trendByCalendarInterval &&
      chartDef.trendByCalendarInterval !== 'auto'
    ) {
      return chartDef.trendByCalendarInterval;
    } else {
      const autoIntervalResponse = responses[0][0].interval as
        | string
        | undefined;
      if (autoIntervalResponse) {
        const autoInterval = toAutoInterval(autoIntervalResponse);
        if (autoInterval) {
          return autoInterval.interval;
        }
      }
    }

    return undefined;
  })();

  if (interval) {
    const currentDateScope = responses[0][0].dateScope as
      | DateRangeInput
      | undefined;
    if (currentDateScope) {
      const validCurrentDates = removePartialIntervalsDates(
        dates,
        interval,
        currentDateScope,
      );
      responses[0] = responses[0].filter((row) =>
        validCurrentDates.includes(row.date as string),
      );
    }

    try {
      const previousDateScope = responses[2][0].dateScope as
        | DateRangeInput
        | undefined;

      if (previousDateScope) {
        const prevDates = responses[2].map((r) => r.date as string);
        const validPreviousDates = removePartialIntervalsDates(
          prevDates,
          interval,
          previousDateScope,
        );
        responses[2] = responses[2].filter((row) =>
          validPreviousDates.includes(row.date as string),
        );
      }
    } catch (ex) {
      const error = new Error();
      error.name = 'Removing partial intervals from previous period failed';
      captureException(error);
    }
  }

  return responses;
};

export default removePartialIntervalResponses;
